import React from "react";

import { colors } from "../../colors";
import { Card } from "../card";
import { Flex } from "../flex";
import { FlexItem } from "../flex-item";
import { Logo } from "../logo";
import { AppName, AppVersion, NavigationSection } from "./styled";
import type { AdminLayoutProps } from "./types";

/**
 * AdminLayout component provides a flexible layout for admin interfaces
 * with a sticky sidebar and main content area.
 *
 * @example
 * ```tsx
 * <AdminLayout
 *   logo={<Logo />}
 *   appName="My Admin Panel"
 *   appVersion="v1.0.0"
 *   navigation={<Navigation />}
 * >
 *   <YourContent />
 * </AdminLayout>
 * ```
 */
export const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  appName,
  appVersion,
  navigation,
  sidebarFooter,
  sidebarWidth = "300px",
  contentMaxWidth = "900px",
  gap = "30px",
  stickyTop = "40px",
  sidebarStyleProps,
  contentStyleProps,
  className,
  testId,
}) => {
  return (
    <Flex
      direction="row"
      gap={gap}
      fullWidth
      {...(className && { className })}
      data-testid={testId}
    >
      <FlexItem flex={`0 0 ${sidebarWidth}`}>
        <Card
          styleProps={{
            background: sidebarStyleProps?.background || colors.white,
            radius: sidebarStyleProps?.radius || "10px",
            boxShadow: sidebarStyleProps?.shadow,
            stickyTop,
          }}
          sticky
          overflowHidden
        >
          <Flex direction="column" gap="0" fullWidth>
            <FlexItem>
              <Card styleProps={{ padding: "30px" }}>
                <Logo />
              </Card>
            </FlexItem>

            {(appName || appVersion) && (
              <FlexItem>
                <Card
                  styleProps={{
                    padding: "20px 30px",
                    borderTop: `1px solid ${colors.gray[100]}`,
                  }}
                >
                  {appName && <AppName>{appName}</AppName>}
                  {appVersion && <AppVersion>v{appVersion}</AppVersion>}
                </Card>
              </FlexItem>
            )}

            {navigation && (
              <FlexItem>
                <NavigationSection>{navigation}</NavigationSection>
              </FlexItem>
            )}

            {sidebarFooter && (
              <FlexItem>
                <Card
                  styleProps={{
                    padding: "20px 30px",
                    borderTop: `1px solid ${colors.gray[100]}`,
                  }}
                >
                  {sidebarFooter}
                </Card>
              </FlexItem>
            )}
          </Flex>
        </Card>
      </FlexItem>

      <FlexItem flex="1" maxWidth={contentMaxWidth}>
        {contentStyleProps ? (
          <Card styleProps={contentStyleProps}>{children}</Card>
        ) : (
          children
        )}
      </FlexItem>
    </Flex>
  );
};
